/**
 * Active-hours window for interrupting notifications (push / desktop).
 *
 * The window says when a notification MAY interrupt, not when it is silenced —
 * the default `07:00 → 18:00` is a working day, and everything outside it stays
 * quiet. Framing it as the active window rather than "quiet hours" removes the
 * off-by-one-in-your-head that inverted ranges invite.
 *
 * `urgent` notifications ignore the window entirely. That exemption is the
 * reason the feature is acceptable at all: a water leak has to reach someone at
 * 3am, and a user who trusts that will not disable the whole channel.
 *
 * Mirror: `backend/functions/src/definitions/notification-quiet-hours.ts` (the
 * backend skips out-of-window pushes) and the inlined copy in
 * `src/firebase-messaging-sw.js` (the worker re-checks against real local time,
 * because the stored timezone goes stale as soon as someone travels). Any
 * change here has to land in all three.
 */

/** A half-open window of local hours: `start` inclusive, `end` exclusive. */
export interface ActiveHours {
  /** First hour that may interrupt, 0-23. */
  readonly start: number;
  /** First hour that may no longer interrupt, 0-23. */
  readonly end: number;
}

/** Working hours. Chosen so the feature is useful before it is configured. */
export const DEFAULT_ACTIVE_HOURS: ActiveHours = { start: 7, end: 18 };

/** Whole hours only — a minute-level window buys nothing here and doubles the UI. */
export const ACTIVE_HOURS_MIN = 0;
export const ACTIVE_HOURS_MAX = 23;

/**
 * Is `hour` inside the window?
 *
 * Supports windows that wrap past midnight (`22 → 6`), because a night
 * caretaker configuring one is not a hypothetical. `start === end` is treated
 * as "always on" rather than "never on": the failure mode of a mis-set slider
 * should be a notification too many, not total silence.
 */
export function isWithinActiveHours(hour: number, { start, end }: ActiveHours): boolean {
  if (start === end) return true;
  if (start < end) return hour >= start && hour < end;
  return hour >= start || hour < end;
}

/**
 * Normalise a stored window, falling back to the default on anything unusable.
 *
 * Preferences are read from a Firestore document that no schema guards, so a
 * hand-edited or half-written value must not be able to silence a user's
 * notifications for good.
 */
export function normaliseActiveHours(value: unknown): ActiveHours {
  const raw = value as { start?: unknown; end?: unknown } | null | undefined;
  const start = Number(raw?.start);
  const end = Number(raw?.end);
  const valid = (n: number): boolean =>
    Number.isInteger(n) && n >= ACTIVE_HOURS_MIN && n <= ACTIVE_HOURS_MAX;
  if (!valid(start) || !valid(end)) return DEFAULT_ACTIVE_HOURS;
  return { start, end };
}
